import { useRef, useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Landmark, RotateCcw } from "lucide-react";

type LinhaPrevia = {
  sequencial: number;
  nossoNumero: string;
  ocorrencia: "06" | "08";
  dataOcorrencia: string | null;
  nomePagador: string;
  boletoId: number | null;
  contaReceberId: number | null;
  parcelaDescricao: string | null;
  destinatarioNome: string | null;
  valorParcela: number | null;
  valorRecebido: number;
  dataRecebimento: string | null;
  status: string;
  mensagem: string;
};

function moeda(valor: number | null | undefined) {
  return Number(valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function dataBr(data: string | null | undefined) {
  if (!data) return "-";
  const [ano, mes, dia] = data.split("-");
  return `${dia}/${mes}/${ano}`;
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    pronto: "Pronto",
    ja_pago: "Já pago",
    nao_localizado: "Não localizado",
    conta_nao_localizada: "Conta não localizada",
    nao_elegivel: "Não elegível",
    duplicado_no_arquivo: "Duplicado no arquivo",
    duplicado_no_banco: "Duplicado no banco",
  };
  return labels[status] ?? status;
}

function statusClass(status: string) {
  if (status === "pronto") return "bg-green-100 text-green-800";
  if (status === "ja_pago") return "bg-blue-100 text-blue-800";
  return "bg-amber-100 text-amber-800";
}

function base64DoArquivo(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      let binary = "";
      for (const byte of bytes) binary += String.fromCharCode(byte);
      resolve(btoa(binary));
    };
    reader.onerror = () => reject(new Error("Não foi possível ler o arquivo."));
    reader.readAsArrayBuffer(file);
  });
}

export default function BankReturnSettlements() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<{ nome: string; conteudoBase64: string } | null>(null);
  const [linhas, setLinhas] = useState<LinhaPrevia[]>([]);
  const [conta, setConta] = useState<any>(null);

  const previaMutation = trpc.baixasRetorno.previaItauCnab400.useMutation({
    onError: (erro) => toast.error(erro.message),
  });
  const confirmarMutation = trpc.baixasRetorno.confirmarItauCnab400.useMutation({
    onSuccess: (resultado) => {
      toast.success(`${resultado.baixados} baixa(s) confirmada(s).`);
      setLinhas((atual) => atual.map((linha) => linha.status === "pronto" ? {
        ...linha,
        status: "ja_pago",
        mensagem: "Baixado por retorno.",
      } : linha));
    },
    onError: (erro) => toast.error(erro.message),
  });

  const prontas = linhas.filter((linha) => linha.status === "pronto");
  const podeConfirmar = prontas.length > 0 && prontas.every((linha) =>
    !!linha.dataRecebimento && /^\d{4}-\d{2}-\d{2}$/.test(linha.dataRecebimento) && Number(linha.valorRecebido) >= 0
  );

  async function abrirPrevia(file: File) {
    try {
      const conteudoBase64 = await base64DoArquivo(file);
      const resultado = await previaMutation.mutateAsync({ conteudoBase64 });
      setArquivo({ nome: file.name, conteudoBase64 });
      setConta(resultado.contaBancaria);
      setLinhas(resultado.linhas as LinhaPrevia[]);
      if (resultado.linhas.length === 0) toast.info("O arquivo não possui ocorrências 06 ou 08.");
    } catch (erro: any) {
      toast.error(erro.message || "Erro ao ler o retorno.");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function atualizarLinha(sequencial: number, campo: "dataRecebimento" | "valorRecebido", valor: string) {
    setLinhas((atual) => atual.map((linha) => {
      if (linha.sequencial !== sequencial) return linha;
      return {
        ...linha,
        [campo]: campo === "valorRecebido" ? Number(valor) : valor,
      };
    }));
  }

  function limpar() {
    setArquivo(null);
    setConta(null);
    setLinhas([]);
  }

  function confirmar() {
    if (!arquivo || !podeConfirmar) return;
    confirmarMutation.mutate({
      conteudoBase64: arquivo.conteudoBase64,
      linhas: prontas.map((linha) => ({
        sequencial: linha.sequencial,
        boletoId: linha.boletoId!,
        contaReceberId: linha.contaReceberId!,
        dataRecebimento: linha.dataRecebimento!,
        valorRecebido: Number(linha.valorRecebido),
      })),
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Baixas Retorno</h1>
            <p className="text-sm text-muted-foreground">Retorno Itaú CNAB 400 — ocorrências 06 e 08</p>
          </div>
        </div>
        {arquivo && <Button variant="outline" onClick={limpar}><RotateCcw className="h-4 w-4 mr-2" />Novo retorno</Button>}
      </div>

      {!arquivo ? (
        <Card>
          <CardContent className="py-10 text-center space-y-4">
            <p className="text-sm text-muted-foreground">Envie o arquivo para abrir a prévia. Nenhuma baixa é feita nesta etapa.</p>
            <Button onClick={() => inputRef.current?.click()} disabled={previaMutation.isPending}>
              <FileUp className="h-4 w-4 mr-2" />
              {previaMutation.isPending ? "Lendo retorno..." : "Enviar retorno"}
            </Button>
            <input ref={inputRef} type="file" className="hidden" onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) abrirPrevia(file);
            }} />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="py-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span><strong>Arquivo:</strong> {arquivo.nome}</span>
              <span><strong>Conta:</strong> {conta ? `${conta.nomeBanco} — Ag. ${conta.agencia} / Cta. ${conta.conta}` : "não localizada"}</span>
              <span><strong>Prontas:</strong> {prontas.length}</span>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Prévia</CardTitle>
            </CardHeader>
            <CardContent>
              {linhas.length === 0 ? (
                <p className="py-6 text-center text-muted-foreground">Nenhuma ocorrência 06 ou 08 encontrada.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 px-2">Nosso número</th>
                        <th className="py-2 px-2">Parcela / destinatário</th>
                        <th className="py-2 px-2">Data de liquidação</th>
                        <th className="py-2 px-2 text-right">Valor</th>
                        <th className="py-2 px-2">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((linha) => (
                        <tr key={linha.sequencial} className="border-b align-top">
                          <td className="py-2 px-2 font-mono text-xs">{linha.nossoNumero}</td>
                          <td className="py-2 px-2">
                            <div>{linha.parcelaDescricao || "-"}</div>
                            <div className="text-xs text-muted-foreground">{linha.destinatarioNome || linha.nomePagador || "-"}</div>
                          </td>
                          <td className="py-2 px-2">
                            {linha.status === "pronto" ? (
                              <Input type="date" className="w-[150px]" value={linha.dataRecebimento ?? ""} onChange={(event) => atualizarLinha(linha.sequencial, "dataRecebimento", event.target.value)} />
                            ) : dataBr(linha.dataRecebimento)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {linha.status === "pronto" ? (
                              <Input type="number" step="0.01" min="0" className="w-[125px] ml-auto text-right" value={linha.valorRecebido} onChange={(event) => atualizarLinha(linha.sequencial, "valorRecebido", event.target.value)} />
                            ) : moeda(linha.valorRecebido)}
                          </td>
                          <td className="py-2 px-2">
                            <Badge className={statusClass(linha.status)}>{statusLabel(linha.status)}</Badge>
                            <p className="mt-1 text-xs text-muted-foreground">{linha.mensagem}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button onClick={confirmar} disabled={!podeConfirmar || confirmarMutation.isPending}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {confirmarMutation.isPending ? "Confirmando..." : `Confirmar ${prontas.length} baixa(s)`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
